
import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, onValue, off, remove } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';

const PaymentTable = ({ onAddPayment, onEditPayment }) => {
  const [payments, setPayments] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletingPaymentId, setDeletingPaymentId] = useState(null);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const encodedEmail = encodeEmailForPath(currentUser.email);
    const paymentsRef = ref(database, `users/${encodedEmail}/paymentModes`);
    
    const paymentsListener = onValue(paymentsRef, (snapshot) => {
      const paymentsData = [];
      snapshot.forEach((childSnapshot) => {
        paymentsData.push({
          id: childSnapshot.key,
          ...childSnapshot.val()
        });
      });
      setPayments(paymentsData);
      setLoading(false);
    });

    return () => {
      off(paymentsRef);
    };
  }, [currentUser]);

  const handleDeletePayment = async (paymentId, paymentCode) => {
    if (!currentUser) return;
    
    // Confirm deletion
    const confirmed = window.confirm(`Are you sure you want to delete the payment mode "${paymentCode}"? This action cannot be undone.`);
    if (!confirmed) return;

    setDeletingPaymentId(paymentId);

    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const paymentRef = ref(database, `users/${encodedEmail}/paymentModes/${paymentId}`);
      
      await remove(paymentRef);
      
      // Show success alert
      alert(`Payment mode "${paymentCode}" deleted successfully!`);
      
    } catch (error) {
      console.error('Error deleting payment mode:', error);
      alert('Failed to delete payment mode. Please try again.');
    } finally {
      setDeletingPaymentId(null);
    }
  };

  // Styles
  const styles = {
    container: {
      maxWidth: '100%',
      margin: '0 auto',
      padding: '2rem',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      padding: '1.5rem',
      backgroundColor: 'white',
      borderRadius: '6px',
      border: '1px solid #d1d5db',
      fontWeight: '600',
      color: '#374151',
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#2563eb',
      margin: '0',
    },
    addButton: {
      padding: '0.875rem 2rem',
      borderRadius: '8px',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.95rem',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
      transform: 'translateY(0)',
    },
    tableContainer: {
      backgroundColor: 'white',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      overflowX: 'auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.9rem',
      border: '1px solid #d1d5db',
    },
    tableHeader: {
      padding: '1rem 0.75rem',
      textAlign: 'left',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid #1e40af',
    },
    tableCell: {
      padding: '1rem 0.75rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      verticalAlign: 'middle',
      textAlign: 'left',
    },
    noData: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#718096',
      fontSize: '1.1rem',
    },
    srNoCell: {
      fontWeight: '600',
      textAlign: 'center',
      width: '60px',
    },
    actionCell: {
      display: 'flex',
      gap: '0.5rem',
      justifyContent: 'center',
      alignItems: 'left',
      padding: '0.5rem',
    },
    editButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#10b981',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
      transform: 'translateY(0)',
    },
    deleteButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    deleteButtonDisabled: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#9ca3af',
      color: 'white',
      fontWeight: '500',
      cursor: 'not-allowed',
      border: 'none',
      fontSize: '0.8rem',
      opacity: '0.6',
    },
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!currentUser) {
    return (
      <div style={styles.container}>
        <div style={styles.noData}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>🔐</div>
          <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>Payment Modes</h3>
          <p>Please log in to view and manage your payment modes.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Payment Modes</h1>
        <button
          onClick={onAddPayment}
          style={styles.addButton}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
          }}
        >
          + Add Payment Mode
        </button>
      </div>

      <div style={styles.tableContainer}>
        {loading ? (
          <div style={styles.noData}>
            <p>⏳ Loading payment modes...</p>
          </div>
        ) : payments.length > 0 ? (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.tableHeader, ...styles.srNoCell }}>Sr.No</th>
                <th style={styles.tableHeader}>Mode</th>
                <th style={styles.tableHeader}>Description</th>
                <th style={styles.tableHeader}>Action</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment, index) => (
                <tr 
                  key={payment.id} 
                  style={{
                    backgroundColor: index % 2 === 0 ? '#f7fafc' : 'white',
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  <td style={{ ...styles.tableCell, ...styles.srNoCell }}>{index + 1}</td>
                  <td style={styles.tableCell}>{payment.code}</td>
                  <td style={styles.tableCell}>{payment.description}</td>
                  <td style={{ ...styles.tableCell, ...styles.actionCell }}>
                    <button
                      onClick={() => onEditPayment(payment)}
                      style={styles.editButton}
                      title="Edit Payment"
                      disabled={deletingPaymentId === payment.id}
                      onMouseEnter={(e) => {
                        if (deletingPaymentId !== payment.id) {
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (deletingPaymentId !== payment.id) {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                        }
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeletePayment(payment.id, payment.code)}
                      style={deletingPaymentId === payment.id ? styles.deleteButtonDisabled : styles.deleteButton}
                      title={deletingPaymentId === payment.id ? "Deleting..." : "Delete Payment"}
                      disabled={deletingPaymentId === payment.id}
                      onMouseEnter={(e) => {
                        if (deletingPaymentId !== payment.id) {
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (deletingPaymentId !== payment.id) {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
                        }
                      }}
                    >
                      {deletingPaymentId === payment.id ? '⏳' : '🗑️'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={styles.noData}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>💳</div>
            <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>No Payment Modes Found</h3>
            <p style={{ marginBottom: '1.5rem' }}>Get started by adding your first payment mode!</p>
            <button 
              onClick={onAddPayment}
              style={{
                ...styles.addButton,
                fontSize: '1rem',
                padding: '1rem 2rem'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
              }}
            >
              + Add Your First Payment Mode
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentTable;